<template>
  <div class="location-picker">
    <div class="map-container" style="height: 400px; width: 100%; position: relative;">
      <l-map
        ref="map"
        :zoom="zoom"
        :center="center"
        :options="mapOptions"
        @click="onMapClick"
        @ready="onMapReady"
        style="height: 100%; width: 100%;"
      >
        <l-tile-layer
          :url="tileLayerUrl"
          :attribution="attribution"
        />

        <!-- Marker for selected location -->
        <l-marker
          v-if="marker"
          :lat-lng="marker"
          :draggable="true"
          @drag="onMarkerDrag"
          @dragend="onMarkerDragEnd"
        >
          <l-popup>
            <div>
              <strong>Selected Location</strong><br>
              Lat: {{ marker.lat.toFixed(6) }}<br>
              Lng: {{ marker.lng.toFixed(6) }}
            </div>
          </l-popup>
        </l-marker>

        <!-- Current location marker (if available) -->
        <l-marker
          v-if="currentLocation"
          :lat-lng="currentLocation"
          :icon="currentLocationIcon"
        >
          <l-popup>
            <div>
              <strong>Your Current Location</strong><br>
              <small>Click to use this location</small>
            </div>
          </l-popup>
        </l-marker>
      </l-map>

      <!-- Map controls -->
      <div class="map-controls" style="position: absolute; top: 10px; right: 10px; z-index: 1000;">
        <v-btn
          small
          color="primary"
          @click="getCurrentLocation"
          :loading="locating"
          style="margin-bottom: 5px;"
        >
          <v-icon small>mdi-crosshairs-gps</v-icon>
          My Location
        </v-btn>
        <br>
        <v-btn
          small
          color="secondary"
          @click="clearLocation"
          :disabled="!marker"
        >
          <v-icon small>mdi-close</v-icon>
          Clear
        </v-btn>
      </div>
    </div>

    <!-- Coordinate display -->
    <v-card class="mt-3" outlined>
      <v-card-text class="py-2">
        <v-row align="center">
          <v-col cols="6">
            <v-text-field
              v-model.number="displayLatitude"
              label="Latitude"
              type="number"
              step="0.000001"
              readonly
              dense
              outlined
              hide-details
            />
          </v-col>
          <v-col cols="6">
            <v-text-field
              v-model.number="displayLongitude"
              label="Longitude"
              type="number"
              step="0.000001"
              readonly
              dense
              outlined
              hide-details
            />
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import { LMap, LTileLayer, LMarker, LPopup, LIcon } from 'vue2-leaflet'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'

// Fix for default markers in webpack
delete L.Icon.Default.prototype._getIconUrl
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png')
})

export default {
  name: 'LocationPicker',
  components: {
    LMap,
    LTileLayer,
    LMarker,
    LPopup,
    LIcon
  },
  props: {
    value: {
      type: Object,
      default: () => ({ latitude: null, longitude: null })
    },
    initialCenter: {
      type: Object,
      default: () => ({ lat: 4.0511, lng: 9.7679 }) // Default to Cameroon
    },
    zoom: {
      type: Number,
      default: 10
    },
    height: {
      type: String,
      default: '400px'
    }
  },
  data () {
    return {
      map: null,
      marker: null,
      currentLocation: null,
      locating: false,
      mapOptions: {
        zoomControl: true,
        attributionControl: true
      },
      tileLayerUrl: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      currentLocationIcon: L.divIcon({
        className: 'current-location-marker',
        html: '<div style="background-color: #1976d2; width: 20px; height: 20px; border-radius: 50%; border: 3px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.3);"></div>',
        iconSize: [20, 20],
        iconAnchor: [10, 10]
      })
    }
  },
  computed: {
    center () {
      if (this.marker) {
        return this.marker
      }
      return this.initialCenter
    },
    displayLatitude: {
      get () {
        return this.marker ? this.marker.lat.toFixed(6) : ''
      },
      set (value) {
        if (this.marker && value) {
          this.marker.lat = parseFloat(value)
          this.updateParent()
        }
      }
    },
    displayLongitude: {
      get () {
        return this.marker ? this.marker.lng.toFixed(6) : ''
      },
      set (value) {
        if (this.marker && value) {
          this.marker.lng = parseFloat(value)
          this.updateParent()
        }
      }
    }
  },
  watch: {
    value: {
      handler (newValue) {
        if (newValue && newValue.latitude && newValue.longitude) {
          this.marker = L.latLng(newValue.latitude, newValue.longitude)
          if (this.map) {
            this.map.setView(this.marker, this.map.getZoom())
          }
        }
      },
      immediate: true,
      deep: true
    },
    initialCenter: {
      handler (newCenter) {
        if (this.map && !this.marker) {
          this.map.setView(newCenter, this.map.getZoom())
        }
      },
      deep: true
    }
  },
  methods: {
    onMapReady () {
      this.map = this.$refs.map.mapObject

      // Set initial marker if coordinates are provided
      if (this.value && this.value.latitude && this.value.longitude) {
        this.marker = L.latLng(this.value.latitude, this.value.longitude)
        this.map.setView(this.marker, this.zoom)
      }
    },

    onMapClick (event) {
      this.marker = event.latlng
      this.updateParent()
    },

    onMarkerDrag (event) {
      this.marker = event.target.getLatLng()
      this.updateParent()
    },

    onMarkerDragEnd (event) {
      this.marker = event.target.getLatLng()
      this.updateParent()
    },

    getCurrentLocation () {
      if (!navigator.geolocation) {
        this.$emit('error', 'Geolocation is not supported by this browser')
        return
      }

      this.locating = true

      navigator.geolocation.getCurrentPosition(
        (position) => {
          this.currentLocation = L.latLng(
            position.coords.latitude,
            position.coords.longitude
          )

          // Center map on current location
          if (this.map) {
            this.map.setView(this.currentLocation, 15)
          }

          this.locating = false
        },
        (error) => {
          this.locating = false
          let message = 'Unable to retrieve your location'

          switch (error.code) {
            case error.PERMISSION_DENIED:
              message = 'Location access denied by user'
              break
            case error.POSITION_UNAVAILABLE:
              message = 'Location information unavailable'
              break
            case error.TIMEOUT:
              message = 'Location request timed out'
              break
          }

          this.$emit('error', message)
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 60000
        }
      )
    },

    clearLocation () {
      this.marker = null
      this.currentLocation = null
      this.updateParent()
    },

    updateParent () {
      const coordinates = this.marker
        ? {
            latitude: this.marker.lat,
            longitude: this.marker.lng
          }
        : { latitude: null, longitude: null }

      this.$emit('input', coordinates)
      this.$emit('change', coordinates)
    },

    // Public method to set location programmatically
    setLocation (latitude, longitude) {
      if (latitude && longitude) {
        this.marker = L.latLng(latitude, longitude)
        if (this.map) {
          this.map.setView(this.marker, this.map.getZoom())
        }
        this.updateParent()
      }
    },

    // Public method to center map on specific coordinates
    centerMap (latitude, longitude, zoom = null) {
      if (this.map) {
        const newCenter = L.latLng(latitude, longitude)
        this.map.setView(newCenter, zoom || this.map.getZoom())
      }
    }
  },

  mounted () {
    // Initialize with value if provided
    if (this.value && this.value.latitude && this.value.longitude) {
      this.marker = L.latLng(this.value.latitude, this.value.longitude)
    }
  }
}
</script>

<style scoped>
.location-picker {
  width: 100%;
}

.map-container {
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  border: 1px solid #e0e0e0;
}

.map-controls {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

/* Custom styles for the map */
::v-deep .leaflet-container {
  font-family: inherit;
  background: #f5f5f5;
}

::v-deep .leaflet-popup-content-wrapper {
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
}

::v-deep .leaflet-popup-content {
  margin: 8px 12px;
  line-height: 1.4;
  font-size: 13px;
}

::v-deep .leaflet-popup-tip {
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

::v-deep .leaflet-control-zoom a {
  background-color: white;
  border: 1px solid #ccc;
  color: #333;
  font-weight: bold;
}

::v-deep .leaflet-control-zoom a:hover {
  background-color: #f0f0f0;
  border-color: #999;
}

/* Current location marker animation */
@keyframes pulse {
  0% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.2);
    opacity: 0.7;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

::v-deep .current-location-marker {
  animation: pulse 2s infinite;
}

/* Coordinate display styling */
.coordinate-display {
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 4px;
}

/* Loading state for map */
.map-loading {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 1000;
  background: rgba(255, 255, 255, 0.9);
  padding: 10px 20px;
  border-radius: 4px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .map-controls {
    position: absolute;
    bottom: 10px;
    right: 10px;
    flex-direction: row;
    gap: 5px;
  }

  .map-controls .v-btn {
    min-width: auto;
    padding: 0 8px;
  }
}
</style>
